import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { createDateWithoutTimezone } from '@omnea-app/packages/universal/date-utils/createDateWithoutTimezone'
import { PriceFormAnswerValue } from '@omnea-app/packages/universal/form/elements/price'
import { calculateRequestDuration } from '@omnea-app/packages/universal/requests/calculateRequestDuration'
import { getIsRenewalCancelled } from '@omnea-app/packages/universal/requests/getIsRenewalCancelled'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { ListRequestsBody, RequestTopLevelFilterKey } from '@omnea-app/requests/requestsFrontendClient'
import { RequestListItem, RequestPriority } from '@omnea-app/requests/requestsHttpClient'
import { SummaryRiskLevel } from '@omnea-app/risks/risksFrontendClient'
import { useAllRequests } from '../useRequestDetail'

type RequestRowSupplier = { name: string; logoUrl: string | null }
type DeletedRequestRowSupplier = { id: string; isDeleted: true }

export const isDeletedRequestRowSupplier = (
  supplier: RequestRowSupplier | DeletedRequestRowSupplier | null
): supplier is DeletedRequestRowSupplier => {
  return isDefined(supplier) && 'isDeleted' in supplier && supplier.isDeleted
}

export type RequestRow = {
  id: string
  name: string
  supplier: RequestRowSupplier | DeletedRequestRowSupplier | null
  workflowTitle: string
  createdBy: string
  stage: string[]
  currentUserAssignees: { id: string; name: string }[]
  currentGroupAssignees: { id: string; name: string; userIds: string[] }[]
  allCurrentAssignees: string[]
  isUserAssigned: boolean
  followers: { id: string; name: string }[]
  isFollowedByCurrentUser: boolean
  inProgressStepCompletionDate: Date | null
  estimatedCompletionTime: Date | null
  createdAt: string
  completedAt: string | null
  delayed: boolean
  projectOwner: RequestListItem['participants']['projectOwner']
  leads: RequestListItem['participants']['leads']
  requestor: RequestListItem['participants']['requestor']
  rejected: boolean
  paused: boolean
  pausedAt: string | null
  isRequestOfUser: boolean
  totalStages: number
  completedStages: number
  riskCount: number
  riskScore: number
  autoGeneratedTags: string[]
  department: { name: string } | null
  costCenter: { name: string } | null
  goLiveDate: string | null
  inBudget: boolean | null
  value: RequestListItem['value']
  autoRenewal: boolean | null
  budgetHolder: { name: string } | null
  businessOwner: { name: string } | null
  contractStartDate: string | null
  cancellationReason: string | null
  contractEndDate: string | null
  buyerLegalEntity: { name: string } | null
  paymentTerm: { name: string } | null
  paymentMethod: { name: string } | null
  poNumber: string | null
  cancellationNotice: number | null
  note: string | null
  type: string | null
  tags: string[]
  publicId: string
  renewalDate: Date | null
  priority: RequestPriority
  latestComment: {
    id: string
    text: string
    user: { id: string; firstName: string; lastName: string }
    createdAt: string
    mentions: {
      id: string
      isResolved: boolean
      userId: string | null
      user: {
        firstName: string
        lastName: string
        email: string
        isOutOfOffice: boolean
      } | null
    }[]
  } | null
  negotiatedSavings: PriceFormAnswerValue | null
  costAvoidance: PriceFormAnswerValue | null
  financeBudget: PriceFormAnswerValue | null
  product: { id: string; name: string | null } | null
  budget: { id: string; name: string | null } | null
  shouldRenew: boolean | null
  purchaseOrderNumber: string | null
  cancelBy: Date | null
  currency: string | null
  annualValue: string | null
  productService: string | null
  renewal: boolean | null
  status: RequestListItem['derivedFields']['status']
  risks: SummaryRiskLevel[]
  inherentRisk: number | null
  residualRisk: number | null
  category: string | null
  categoryPath: string | null
  market: string | null
  duration: { days: number; hours: number; minutes: number } | null
  hasRejectedTasks: boolean
  hasErroredTasks: boolean
  remotePOInvoicedValue: number | null
  remotePOPaidValue: number | null
  remotePOStatus: string | null
  remotePOTotalValue: number | null
  remoteCurrency: CurrencyCode | null
  purchaseOrderAwaitingReceipt: boolean | null
  operationalStatusAssessment: 'ON_TRACK' | 'SLOW' | 'NEEDS_ATTENTION' | 'BLOCKED' | null
}

const getSupplier = (
  request: RequestListItem
): { name: string; logoUrl: string | null } | { id: string; isDeleted: true } | null => {
  if (isDefined(request.supplierId) && isNil(request.supplier)) {
    return { id: request.supplierId, isDeleted: true }
  }
  return request.supplier
}

export const requestDataToRequestRows = (requests: RequestListItem[] = [], userId: string): RequestRow[] => {
  return requests.map((request: RequestListItem): RequestRow => {
    const currentStepNames = request.derivedFields.inProgressSteps.map((step) => step.name)
    const isUserAssigned =
      request.derivedFields.currentUserAssignees.some((assignee) => assignee.id === userId) ||
      request.derivedFields.currentGroupAssignees.some((group) => group.userIds.includes(userId))

    const allCurrentAssignees = [
      ...(request.derivedFields.currentUserAssignees ?? []).map((u) => u.name),
      ...(request.derivedFields.currentGroupAssignees ?? []).map((g) => g.name),
    ]

    return {
      id: request.rawData.id,
      name: request.rawData.name,
      supplier: getSupplier(request),
      createdBy: request.participants.requestor.name,
      workflowTitle: request.workflow.name,
      stage: currentStepNames,
      currentUserAssignees: request.derivedFields.currentUserAssignees,
      currentGroupAssignees: request.derivedFields.currentGroupAssignees,
      allCurrentAssignees,
      isUserAssigned,
      inProgressStepCompletionDate: request.derivedFields.inProgressStepCompletionDate
        ? new Date(request.derivedFields.inProgressStepCompletionDate)
        : null,
      estimatedCompletionTime: request.derivedFields.estimatedCompletionDate
        ? new Date(request.derivedFields.estimatedCompletionDate)
        : null,
      createdAt: request.timing.createdAt,
      completedAt: request.timing.completedAt,
      delayed: request.timing.isDelayed,
      rejected: request.derivedFields.isRejected,
      paused: request.derivedFields.isPaused,
      pausedAt: request.derivedFields.pausedAt,
      isRequestOfUser: request.participants.requestor.id === userId,
      isFollowedByCurrentUser: request.derivedFields.followers.some((follower) => follower.user.id === userId),
      followers: request.derivedFields.followers.map((follower) => follower.user),
      projectOwner: request.participants.projectOwner,
      requestor: request.participants.requestor,
      totalStages: request.derivedFields.totalStages,
      completedStages: request.derivedFields.completedStages,
      riskCount: request.derivedFields.risk.numberOfFlaggedQuestions,
      riskScore: request.derivedFields.risk.aggregate,
      autoGeneratedTags: request.derivedFields.tags,
      department: request.department,
      costCenter: request.costCenter,
      goLiveDate: request.goLiveDate,
      inBudget: request.inBudget,
      budgetHolder: request.participants.budgetHolder,
      businessOwner: request.participants.businessOwner,
      cancellationReason: request.cancellationReason,
      contractStartDate: request.contractStartDate,
      contractEndDate: request.contractEndDate,
      buyerLegalEntity: request.buyerLegalEntity,
      paymentTerm: request.paymentTerms,
      paymentMethod: request.paymentMethod,
      poNumber: request.purchaseOrder?.remoteNumber ?? request.poNumber ?? request.engagement?.poNumber ?? null,
      cancellationNotice: request.cancellationNotice,
      value: request.value,
      note: request.note,
      type: request.type,
      tags: request.derivedFields.tagsV2.map((tag) => tag.name),
      publicId: request.rawData.publicId,
      renewalDate: request.renewal?.renewalDate ? createDateWithoutTimezone(request.renewal.renewalDate) : null,
      priority: request.derivedFields.priority,
      latestComment: request.derivedFields.latestComment || null,
      negotiatedSavings: (request.derivedFields.tableFormResponses as Record<string, any>)?.negotiatedSavings?.value || null,
      costAvoidance: (request.derivedFields.tableFormResponses as Record<string, any>)?.costAvoidance?.value || null,
      financeBudget: (request.derivedFields.tableFormResponses as Record<string, any>)?.financeBudget?.value || null,
      product: request.product ? { id: request.product.id, name: request.product.name } : null,
      budget: request.budget,
      shouldRenew: request.derivedFields.shouldRenew,
      autoRenewal: request.isAutoRenewal,
      cancelBy: request.renewal?.cancellationNoticeDate
        ? createDateWithoutTimezone(request.renewal.cancellationNoticeDate)
        : null,
      currency: request.value?.currency ?? null,
      annualValue: request.value ? String(request.value.amount) : null,
      productService: request.product?.name ?? null,
      renewal: request.derivedFields.shouldRenew,
      purchaseOrderNumber: request.poNumber,
      status: request.derivedFields.status,
      risks: request.risks,
      inherentRisk: null,
      residualRisk: null,
      category: request.category ? (typeof request.category === 'object' ? request.category.value : request.category) : null,
      categoryPath: request.category
        ? typeof request.category === 'object'
          ? (request.category.path ?? null)
          : request.category
        : null,
      market: request.market ? (typeof request.market === 'object' ? request.market.value : request.market) : null,
      duration: calculateRequestDuration({
        createdAt: request.timing.createdAt,
        completedAt: request.timing.completedAt,
        pausedAt: request.derivedFields.pausedAt,
        status: request.derivedFields.status,
      }),
      leads: request.participants.leads,
      hasRejectedTasks: request.derivedFields.hasRejectedTasks,
      hasErroredTasks: request.derivedFields.hasErroredTasks,
      remotePOInvoicedValue: request.purchaseOrder?.remoteInvoicedValue ?? null,
      remotePOPaidValue: request.purchaseOrder?.remotePaidValue ?? null,
      remotePOStatus: request.purchaseOrder?.remoteStatus ?? null,
      remotePOTotalValue: request.purchaseOrder?.remoteTotalValue ?? null,
      remoteCurrency: request.purchaseOrder?.currency ?? null,
      purchaseOrderAwaitingReceipt: request.purchaseOrder?.awaitingReceipt ?? null,
      operationalStatusAssessment: request.derivedFields.operationalStatusAssessment ?? null,
    }
  })
}

export const filterByTopLevelFilter = (
  request: RequestRow,
  userId: string,
  userIsAdmin: boolean,
  topLevelFilter?: RequestTopLevelFilterKey | null
) => {
  switch (topLevelFilter) {
    case 'all':
    case null:
    case undefined:
      return true

    case 'mine':
      return request.isRequestOfUser

    case 'pending':
      return isNil(request.completedAt) && !request.rejected && !request.paused

    case 'delayed':
      return request.delayed && !request.rejected && !request.paused

    case 'rejected':
      return request.rejected

    case 'rejected-tasks':
      return request.hasRejectedTasks && isNil(request.completedAt) && !request.rejected

    case 'errored-tasks':
      return request.hasErroredTasks && isNil(request.completedAt) && !request.rejected

    case 'cancelled':
      return getIsRenewalCancelled({ isCompleted: !isNil(request.completedAt), shouldRenew: request.shouldRenew })

    case 'completed':
      return !isNil(request.completedAt)

    case 'paused':
      return request.paused

    case 'minePending':
      return request.isRequestOfUser && isNil(request.completedAt) && !request.rejected

    case 'mineDelayed':
      return request.isRequestOfUser && request.delayed && !request.rejected

    case 'mineCancelled':
      return (
        request.isRequestOfUser &&
        getIsRenewalCancelled({ isCompleted: !isNil(request.completedAt), shouldRenew: request.shouldRenew })
      )

    case 'mineCompleted':
      return (
        request.isRequestOfUser &&
        !isNil(request.completedAt) &&
        !getIsRenewalCancelled({ isCompleted: !isNil(request.completedAt), shouldRenew: request.shouldRenew })
      )

    case 'minePaused':
      return request.isRequestOfUser && request.paused

    case 'awaiting-receipt':
      return request.purchaseOrderAwaitingReceipt === true

    case 'following':
      return request.isFollowedByCurrentUser

    case 'owned':
      if (request.leads.length > 0) {
        return request.leads.some((lead) => lead?.id === userId)
      } else if (userIsAdmin) {
        return true
      } else {
        return false
      }

    default:
      assertNever(topLevelFilter)
      return true
  }
}

export const useRequestTableRowsForCurrentUser = (requestsBody?: ListRequestsBody, supplierId?: string) => {
  const { user } = useAuthenticatedUser()

  const {
    data: allRequests,
    isLoading: isLoadingAll,
    isFetching,
    isSuccess,
    isError,
    refetch,
  } = useAllRequests(requestsBody, supplierId)

  const allRequestRows = useMemo(
    () =>
      requestDataToRequestRows(allRequests, user.id).filter((row) =>
        filterByTopLevelFilter(row, user.id, user.isAdmin, requestsBody?.topLevelFilter)
      ),
    // user id and isAdmin are stable (because they're primitives), whereas user itself is not
    [allRequests, requestsBody?.topLevelFilter, user.id, user.isAdmin]
  )

  return {
    data: {
      rows: allRequestRows,
      paginationMode: 'client' as const,
      totalCount: undefined,
    },
    loading: isLoadingAll,
    isFetching,
    isSuccess,
    isError,
    refetch,
  }
}
